import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Clock4, Wallet, Percent, Cpu, ShieldCheck,
  Headphones, Wrench, ArrowRight, Search, ClipboardList, Rocket,
  CircleDollarSign, Sparkles, TrendingUp, Target, Users, Zap,
  MessageCircleHeart, Compass, BadgeCheck, ChevronRight,
} from "lucide-react";
import { Logo } from "../components/AppShell";

const BENEFITS = [
  { icon: GraduationCap, title: "Formación incluida", desc: "Te entregamos un onboarding claro, materiales de venta y plantillas listas para que arranques sin fricción." },
  { icon: Clock4, title: "Trabajo flexible", desc: "Tú decides tus horarios, tu volumen y desde dónde trabajas. Sin reportes ni horarios fijos." },
  { icon: Wallet, title: "Pagos semanales", desc: "Cada semana liquidamos las comisiones generadas por las ventas confirmadas y cobradas." },
  { icon: Percent, title: "Comisiones hasta 50%", desc: "Reparto justo: 50/50 sobre el valor del proyecto vendido. Sin letra pequeña." },
  { icon: Cpu, title: "Sistema listo para vender", desc: "Tu link de afiliado único, panel propio y onboarding automatizado para tus clientes." },
  { icon: ShieldCheck, title: "Sin experiencia obligatoria", desc: "Si tienes actitud comercial y red social/local, te formamos para que cierres ventas reales." },
  { icon: Headphones, title: "Soporte constante", desc: "Equipo dedicado por WhatsApp y email para resolver dudas y acompañarte en cada venta." },
  { icon: Wrench, title: "Herramientas digitales incluidas", desc: "Recursos, ejemplos, scripts comerciales y materiales gráficos descargables desde tu panel." },
];

const STEPS = [
  { n: "01", icon: Search, title: "Encuentra un negocio interesado", desc: "Identifica comercios, autónomos o pymes en Madrid que necesiten mejorar su presencia digital." },
  { n: "02", icon: ClipboardList, title: "El cliente completa el onboarding", desc: "Compartes tu link único `?ref=ELM####` y el cliente realiza el onboarding paso a paso desde el móvil." },
  { n: "03", icon: Rocket, title: "En Línea Madrid desarrolla el proyecto", desc: "Nuestro equipo se encarga del diseño, desarrollo y entrega. Tú no tienes que tocar nada técnico." },
  { n: "04", icon: CircleDollarSign, title: "Recibes comisión semanal", desc: "Cuando el cliente paga, tu comisión queda confirmada y se liquida en el ciclo semanal." },
];

const PROFILE = [
  { icon: MessageCircleHeart, t: "Buena comunicación" },
  { icon: BadgeCheck, t: "Responsable" },
  { icon: Sparkles, t: "Motivado/a" },
  { icon: TrendingUp, t: "Ganas de crecer" },
  { icon: Target, t: "Interés en ventas o marketing" },
  { icon: Compass, t: "Iniciativa propia" },
];

const SEO_TITLE = "Afiliados Madrid — Gana dinero por comisiones | En Línea Madrid";
const SEO_DESC = "Programa de afiliados En Línea Madrid: gana hasta el 50% de comisión ayudando a negocios locales de Madrid a crecer online. Trabajo flexible, pagos semanales, sin experiencia obligatoria. Únete hoy.";
const SEO_KEYWORDS = "trabajo por comisiones madrid, ganar dinero online madrid, afiliados madrid, vendedores comisiones madrid, trabajo flexible madrid, ingresos extra madrid, ventas digitales madrid, afiliado digital, partner comercial madrid, emprendimiento digital madrid, trabajo remoto parcial";

function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = SEO_TITLE;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const setMeta = (name, content, prop = "name") => {
      const sel = `meta[${prop}="${name}"]`;
      const el = ensure(sel, () => {
        const m = document.createElement("meta");
        m.setAttribute(prop, name);
        return m;
      });
      el.setAttribute("content", content);
      return el;
    };

    setMeta("description", SEO_DESC);
    setMeta("keywords", SEO_KEYWORDS);
    setMeta("robots", "index,follow,max-image-preview:large");
    setMeta("author", "En Línea Madrid");
    setMeta("geo.region", "ES-MD");
    setMeta("geo.placename", "Madrid");

    // Open Graph
    setMeta("og:title", SEO_TITLE, "property");
    setMeta("og:description", SEO_DESC, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "es_ES", "property");
    setMeta("og:site_name", "En Línea Madrid", "property");
    setMeta("og:url", `${window.location.origin}/vendedores`, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", SEO_TITLE);
    setMeta("twitter:description", SEO_DESC);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}/vendedores`);

    // JSON-LD structured data
    const ldId = "elm-affiliate-ld";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = ldId;
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "name": "En Línea Madrid",
          "url": `${window.location.origin}`,
          "logo": `${window.location.origin}/favicon.ico`,
          "areaServed": { "@type": "City", "name": "Madrid" },
          "sameAs": [],
        },
        {
          "@type": "WebPage",
          "name": SEO_TITLE,
          "description": SEO_DESC,
          "inLanguage": "es-ES",
          "url": `${window.location.origin}/vendedores`,
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Cuánto puedo ganar como afiliado en Madrid?",
              "acceptedAnswer": { "@type": "Answer", "text": "Las comisiones llegan al 50% del valor del proyecto vendido. El ingreso depende del volumen de ventas que generes con tu link único de afiliado." },
            },
            {
              "@type": "Question",
              "name": "¿Necesito experiencia previa en ventas?",
              "acceptedAnswer": { "@type": "Answer", "text": "No es obligatorio. Te entregamos formación, materiales comerciales y soporte directo para que arranques desde cero." },
            },
            {
              "@type": "Question",
              "name": "¿Cuándo se pagan las comisiones?",
              "acceptedAnswer": { "@type": "Answer", "text": "Las comisiones se liquidan semanalmente sobre las ventas confirmadas y cobradas." },
            },
            {
              "@type": "Question",
              "name": "¿Cómo se hace seguimiento de mis ventas?",
              "acceptedAnswer": { "@type": "Answer", "text": "Recibes un link único con tu código de afiliado (ej. ?ref=ELM0001). Cualquier cliente que llegue por ese link queda asociado a tu cuenta automáticamente." },
            },
          ],
        },
      ],
    });

    return () => {
      document.title = prevTitle;
      const node = document.getElementById(ldId);
      if (node) node.remove();
    };
  }, []);
}

export default function VendorLanding() {
  useSEO();

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-[var(--el-red)] selection:text-white">
      <style>{`
        @keyframes elm-fade-up { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform: none; } }
        @keyframes elm-pulse-ring { 0% { box-shadow: 0 0 0 0 rgba(155,34,38,.35); } 70% { box-shadow: 0 0 0 18px rgba(155,34,38,0); } 100% { box-shadow: 0 0 0 0 rgba(155,34,38,0); } }
        @keyframes elm-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .elm-fade-up { animation: elm-fade-up .7s cubic-bezier(.2,.7,.2,1) both; }
        .elm-stagger > * { opacity: 0; animation: elm-fade-up .65s cubic-bezier(.2,.7,.2,1) forwards; }
        .elm-stagger > *:nth-child(1){ animation-delay: .05s; }
        .elm-stagger > *:nth-child(2){ animation-delay: .12s; }
        .elm-stagger > *:nth-child(3){ animation-delay: .19s; }
        .elm-stagger > *:nth-child(4){ animation-delay: .26s; }
        .elm-stagger > *:nth-child(5){ animation-delay: .33s; }
        .elm-stagger > *:nth-child(6){ animation-delay: .40s; }
        .elm-stagger > *:nth-child(7){ animation-delay: .47s; }
        .elm-stagger > *:nth-child(8){ animation-delay: .54s; }
        .elm-cta-ring { animation: elm-pulse-ring 2.4s ease-out infinite; }
        .elm-float { animation: elm-float 5s ease-in-out infinite; }
        .elm-grid-bg {
          background-image:
            radial-gradient(circle at 1px 1px, rgba(15,23,42,.06) 1px, transparent 0);
          background-size: 22px 22px;
        }
        .elm-card { transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s, border-color .35s; }
        .elm-card:hover { transform: translateY(-3px); box-shadow: 0 16px 40px -16px rgba(15,23,42,.18); }
      `}</style>

      {/* HEADER */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/85 border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo-home">
            <Logo size={32} withWordmark />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600 font-medium">
            <a href="#beneficios" className="hover:text-slate-900 transition-colors">Beneficios</a>
            <a href="#como-funciona" className="hover:text-slate-900 transition-colors">Cómo funciona</a>
            <a href="#ingresos" className="hover:text-slate-900 transition-colors">Ingresos</a>
            <a href="#perfil" className="hover:text-slate-900 transition-colors">Perfil ideal</a>
          </nav>
          <Link
            to="/vendedores/login"
            data-testid="landing-header-login"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-black transition-colors"
          >
            Acceder <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 elm-grid-bg opacity-70" aria-hidden="true" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--el-red)]/8 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-slate-900/5 blur-3xl" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 elm-fade-up">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[var(--el-red)]/20 bg-[var(--el-red)]/5 text-[var(--el-red)] text-[12px] font-bold uppercase tracking-[0.2em]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--el-red)] animate-pulse" />
              Programa de afiliados · Madrid
            </div>
            <h1 className="mt-5 font-[Outfit] font-extrabold text-slate-900 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Gana dinero ayudando <br className="hidden sm:block" />
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--el-red)]">negocios locales</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[var(--el-red)]/15 -z-0 rounded-sm" aria-hidden="true" />
              </span>{" "}
              a crecer online.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Únete al programa de afiliados de <strong className="text-slate-900">En Línea Madrid</strong> y genera ingresos
              recomendando soluciones digitales reales para negocios en Madrid. Trabajo flexible, pagos semanales
              y comisiones hasta del 50%.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Link
                to="/vendedores/registro"
                data-testid="landing-hero-cta"
                className="group relative inline-flex items-center gap-2 px-7 h-14 rounded-full bg-[var(--el-red)] text-white font-bold text-base elm-cta-ring hover:bg-[var(--el-red-hover)] transition-colors"
              >
                Quiero ser afiliado
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/vendedores/login"
                data-testid="landing-hero-login"
                className="inline-flex items-center gap-2 px-5 h-14 rounded-full border border-slate-300 text-slate-900 font-semibold text-sm hover:border-slate-900 transition-colors"
              >
                Ya tengo cuenta
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Sin coste de inscripción</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Liquidación semanal</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Soporte real</span>
            </div>
          </div>

          {/* Mockup card */}
          <div className="lg:col-span-5 relative">
            <div className="elm-float relative mx-auto max-w-sm">
              <div className="absolute -inset-4 rounded-[36px] bg-gradient-to-br from-[var(--el-red)]/15 via-transparent to-slate-900/10 blur-2xl" aria-hidden="true" />
              <div className="relative rounded-[28px] bg-white border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,.35)] p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Logo size={28} />
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Panel afiliado</div>
                      <div className="font-[Outfit] font-bold text-slate-900 text-sm">Hola, Carlos</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono font-bold tracking-wider px-2 py-1 rounded-md bg-[var(--el-red)]/10 text-[var(--el-red)]">ELM0142</span>
                </div>

                <div className="mt-5 rounded-2xl bg-gradient-to-br from-[var(--el-red)] to-[#6f181b] text-white p-5">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold">Comisión acumulada</div>
                  <div className="font-[Outfit] font-extrabold text-3xl mt-1">487,50 €</div>
                  <div className="mt-2 text-[12px] text-white/80 flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" /> +125 € esta semana
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Clientes</div>
                    <div className="font-[Outfit] font-extrabold text-xl text-slate-900">07</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Conversion</div>
                    <div className="font-[Outfit] font-extrabold text-xl text-slate-900">38%</div>
                  </div>
                </div>

                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Tu link</div>
                    <div className="text-[12px] font-mono text-slate-700 truncate">enlineamadrid.com/onboarding?ref=ELM0142</div>
                  </div>
                  <Zap className="w-4 h-4 text-[var(--el-red)] shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="beneficios" className="relative py-20 sm:py-28 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--el-red)]">Beneficios</div>
            <h2 className="mt-3 font-[Outfit] font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Todo lo que necesitas para empezar a generar ingresos en Madrid
            </h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">
              Un programa de afiliados pensado para vendedores, partners comerciales y emprendedores digitales
              que buscan ingresos extra recomendando servicios reales a negocios locales.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 elm-stagger">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <article key={title} className="elm-card rounded-2xl border border-slate-200 bg-white p-5">
                <div className="w-11 h-11 rounded-xl bg-[var(--el-red)]/8 text-[var(--el-red)] grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-[Outfit] font-bold text-slate-900 text-base">{title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="relative py-20 sm:py-28 bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--el-red)]">Cómo funciona</div>
            <h2 className="mt-3 font-[Outfit] font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight">
              De la recomendación a la comisión en 4 pasos
            </h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">
              Un flujo simple, automatizado y transparente. Tú recomiendas, nosotros entregamos, ambos ganamos.
            </p>
          </div>

          <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 elm-stagger">
            {STEPS.map(({ n, icon: Icon, title, desc }) => (
              <li key={n} className="relative rounded-2xl bg-white border border-slate-200 p-6 elm-card">
                <div className="flex items-center justify-between">
                  <span className="font-[Outfit] font-black text-[44px] leading-none text-slate-200">{n}</span>
                  <div className="w-11 h-11 rounded-xl bg-slate-900 text-white grid place-items-center">
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="mt-4 font-[Outfit] font-bold text-slate-900 text-base">{title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* EARNINGS */}
      <section id="ingresos" className="relative py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--el-red)]">Potencial de ingresos</div>
            <h2 className="mt-3 font-[Outfit] font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Tu esfuerzo, tu escala. Tú decides cuánto crecer.
            </h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">
              Las comisiones se calculan sobre el valor del proyecto cerrado. Cuantos más negocios locales acompañes,
              más se compone tu ingreso recurrente. Los ejemplos siguientes son ilustrativos y no constituyen
              una promesa de ingresos.
            </p>
            <Link
              to="/vendedores/registro"
              data-testid="landing-earnings-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 h-12 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-black transition-colors"
            >
              Calcular mi potencial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {[
              { label: "1 venta", val: "Ingreso rápido", note: "Comisión inmediata tras el cobro del proyecto.", accent: false, big: "75 €+" },
              { label: "5 ventas / mes", val: "Ingreso constante", note: "Flujo mensual estable con cartera activa.", accent: true, big: "375 €+" },
              { label: "Escala mensual", val: "Crecimiento real", note: "Cartera, referidos y volumen sostenido.", accent: false, big: "Sin techo" },
            ].map((c, i) => (
              <article
                key={i}
                className={`rounded-2xl p-6 border elm-card ${
                  c.accent
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white border-slate-200"
                }`}
              >
                <div className={`text-[11px] font-bold uppercase tracking-[0.18em] ${c.accent ? "text-white/60" : "text-slate-500"}`}>
                  {c.label}
                </div>
                <div className={`mt-2 font-[Outfit] font-extrabold text-2xl ${c.accent ? "text-white" : "text-slate-900"}`}>
                  {c.val}
                </div>
                <div className={`mt-3 font-[Outfit] font-black text-3xl ${c.accent ? "text-white" : "text-[var(--el-red)]"}`}>
                  {c.big}
                </div>
                <p className={`mt-2 text-[13px] leading-relaxed ${c.accent ? "text-white/70" : "text-slate-600"}`}>
                  {c.note}
                </p>
              </article>
            ))}
            <p className="sm:col-span-3 text-[11px] text-slate-400 mt-1">
              * Las cifras son orientativas. El ingreso real depende del volumen de ventas confirmadas y del plan
              contratado por cada cliente referido (Profesional, Avanzado o Premium).
            </p>
          </div>
        </div>
      </section>

      {/* IDEAL PROFILE */}
      <section id="perfil" className="relative py-20 sm:py-28 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--el-red)]">Perfil ideal</div>
            <h2 className="mt-3 font-[Outfit] font-extrabold text-3xl sm:text-4xl tracking-tight">
              Buscamos personas con actitud, no con currículum.
            </h2>
            <p className="mt-4 text-white/70 text-base leading-relaxed">
              No necesitas título ni experiencia previa para ser parte. Si tienes red local en Madrid y ganas de
              construir un ingreso digital recurrente, encajas perfectamente con el programa.
            </p>
          </div>
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-3 elm-stagger">
            {PROFILE.map(({ icon: Icon, t }) => (
              <div key={t} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--el-red)]/20 text-[var(--el-red)] grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-[Outfit] font-semibold text-white">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ-style trust */}
      <section className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--el-red)] text-center">Preguntas frecuentes</div>
          <h2 className="mt-3 font-[Outfit] font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight text-center">
            Lo que necesitas saber antes de empezar
          </h2>
          <div className="mt-10 space-y-3">
            {[
              { q: "¿Cuánto puedo ganar como afiliado en Madrid?", a: "Las comisiones llegan hasta el 50% del valor del proyecto vendido. Tu ingreso real depende del volumen y del tipo de plan que contraten los negocios que recomiendes." },
              { q: "¿Necesito experiencia previa en ventas o marketing?", a: "No es obligatorio. Te entregamos formación, materiales y soporte continuo para que arranques aunque sea tu primera experiencia comercial." },
              { q: "¿Cuándo y cómo se pagan las comisiones?", a: "Liquidamos semanalmente las comisiones sobre ventas cobradas. Recibes el detalle directamente en tu panel de vendedor." },
              { q: "¿Cómo se hace el seguimiento de mis ventas?", a: "Al registrarte recibes un código único de afiliado (por ejemplo ELM0142). Compartes tu link personalizado (?ref=ELM0142) y cualquier cliente que llegue por él queda asociado a tu cuenta automáticamente." },
              { q: "¿Necesito facturar o darme de alta como autónomo?", a: "Para que podamos liquidar tus comisiones de forma legal y transparente, necesitas poder emitir factura. Si tienes dudas, te asesoramos en el proceso." },
            ].map((it, i) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-5 [&_summary]:cursor-pointer">
                <summary className="flex items-center justify-between gap-4 font-[Outfit] font-semibold text-slate-900 text-base">
                  {it.q}
                  <ChevronRight className="w-5 h-5 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[var(--el-red)] to-[#5c1416] text-white px-8 py-16 sm:px-14 sm:py-20 text-center">
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} aria-hidden="true" />
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white/90 text-[11px] font-bold uppercase tracking-[0.2em]">
                <Users className="w-3.5 h-3.5" /> Comunidad de afiliados Madrid
              </div>
              <h2 className="mt-5 font-[Outfit] font-extrabold text-3xl sm:text-5xl tracking-tight">
                Empieza hoy con En Línea Madrid
              </h2>
              <p className="mt-4 text-white/85 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                Activa tu cuenta de afiliado en menos de 2 minutos, recibe tu link único y empieza a generar
                comisiones reales ayudando a negocios locales a estar online.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/vendedores/login"
                  data-testid="landing-final-cta"
                  className="inline-flex items-center gap-2 px-7 h-14 rounded-full bg-white text-[var(--el-red)] font-bold text-base hover:bg-slate-100 transition-colors"
                >
                  Acceder como afiliado <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/vendedores/registro"
                  data-testid="landing-final-register"
                  className="inline-flex items-center gap-2 px-6 h-14 rounded-full border border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
                >
                  Crear cuenta nueva
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 py-10 text-sm text-slate-500">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo size={26} withWordmark />
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacidad" className="hover:text-slate-900">Privacidad</Link>
            <Link to="/contacto" className="hover:text-slate-900">Contacto</Link>
            <Link to="/vendedores/login" className="hover:text-slate-900">Acceso afiliados</Link>
          </div>
          <div>© {new Date().getFullYear()} En Línea Madrid · Madrid, España</div>
        </div>
      </footer>
    </div>
  );
}
